import React, { useState } from 'react';
import {
  Container,
  Box,
  Alert,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import ClientSignUpHeader from './client/ClientSignUpHeader';
import ClientSignUpForm from './client/ClientSignUpForm';
import ClientSignUpActions from './client/ClientSignUpActions';

const ClientSignUp = ({ onBack }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // For clients, we create a simpler profile
      await signup(formData.email, formData.password, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        role: 'client',
        salonName: `${formData.firstName} ${formData.lastName}`,
        ownerName: `${formData.firstName} ${formData.lastName}`,
        category: 'Client',
        city: '',
        region: '',
        address: '',
        description: ''
      });

      setSuccess(t('auth.accountCreatedSuccess'));
      // Ne pas rediriger automatiquement - laisser Auth.js gérer la vérification d'email
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      {/* Header Component */}
      <ClientSignUpHeader />

      {/* Content Container - Flex pour une meilleure structure */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#2a2a2a',
          overflow: 'auto'
        }}
      >
        <Container 
          maxWidth="md" 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 2, md: 4 },
            px: { xs: 2, md: 3 }
          }}
        >
          {/* Alerts */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 2, md: 3 },
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                borderRadius: 2,
                fontSize: { xs: '0.875rem', md: '1rem' },
                '& .MuiAlert-icon': {
                  color: '#ef4444'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: { xs: 2, md: 3 },
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                borderRadius: 2,
                fontSize: { xs: '0.875rem', md: '1rem' },
                '& .MuiAlert-icon': {
                  color: '#10b981'
                }
              }}
            >
              {success}
            </Alert>
          )}

          {/* Form Content */}
          <Fade in={true} timeout={300}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-start', md: 'center' },
                maxWidth: { xs: '100%', md: '600px' },
                mx: 'auto',
                width: '100%'
              }}
            >
              {/* Form Component */}
              <ClientSignUpForm
                formData={formData}
                handleInputChange={handleInputChange}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                showConfirmPassword={showConfirmPassword}
                setShowConfirmPassword={setShowConfirmPassword}
                passwordStrength={passwordStrength}
              />

              {/* Actions Component */}
              <ClientSignUpActions
                onBack={onBack}
                onSubmit={handleSubmit}
                loading={loading}
              />
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ClientSignUp;